/**
 * The translated message for a document save the api refused over a line's
 * tax rate — one helper for the expense, AP bill and AR invoice saves, so the
 * user reads why instead of "Failed to create expense".
 *
 * The api answers two codes (docs/TAX.md "A Taxed Line Names Its Rate"):
 *  - `TAX_RATE_ID_REQUIRED` — a taxed line without its rate. Its own pre-check
 *    carries `line_indexes` (0-based); the database backstop (RV025, coded in
 *    `app.onError`) does not.
 *  - `TAX_RATE_UNKNOWN` — a line names a rate the organization does not own
 *    (42501 in the posting functions, coded in `app.onError`).
 *
 * `throwResponseError` / `unwrapEnvelope` hang the 400 body on the thrown error
 * as `envelope`; anything else (a plain Error, another code) is `null` and the
 * caller keeps its generic handling.
 *
 * The caller passes its own `t`: importing `@/i18n` here would pull the
 * initialized i18n instance into every form that checks the code, which makes
 * react-i18next consumers in the subtree suspend under jsdom.
 */
import type { TFunction } from 'i18next';
import { TAX_RATE_ID_REQUIRED, TAX_RATE_UNKNOWN } from '@releve/shared';
import { toast } from '@/hooks/useToast';

/** The codes this helper translates. */
export const TAX_RATE_REFUSAL_CODES: readonly string[] = [TAX_RATE_ID_REQUIRED, TAX_RATE_UNKNOWN];

interface TaxRateRefusalEnvelope {
  code?: string;
  line_indexes?: unknown;
}

function envelopeOf(error: unknown): TaxRateRefusalEnvelope | undefined {
  return (error as { envelope?: TaxRateRefusalEnvelope } | null | undefined)?.envelope;
}

/** Whether `error` carries one of the coded tax-rate refusals (no translation needed). */
export function isTaxRateRefusal(error: unknown): boolean {
  const code = envelopeOf(error)?.code;
  return code !== undefined && TAX_RATE_REFUSAL_CODES.includes(code);
}

/** The translated message for a coded tax-rate refusal, or null. */
export function taxRateRefusalMessage(error: unknown, t: TFunction): string | null {
  const envelope = envelopeOf(error);
  if (envelope?.code === TAX_RATE_UNKNOWN) return t('tax.errors.taxRateUnknown');
  if (envelope?.code !== TAX_RATE_ID_REQUIRED) return null;
  const indexes = Array.isArray(envelope.line_indexes)
    ? envelope.line_indexes.filter((i): i is number => typeof i === 'number')
    : [];
  if (indexes.length === 0) return t('tax.errors.taxRateIdRequired');
  return t('tax.errors.taxRateIdRequiredLines', {
    lines: indexes.map((i) => i + 1).join(', '),
  });
}

/** Toast the translated refusal when `error` is one; `true` when it toasted. */
export function toastTaxRateRefusal(error: unknown, t: TFunction): boolean {
  const message = taxRateRefusalMessage(error, t);
  if (!message) return false;
  toast.error(message);
  return true;
}
